import { MantineTheme, useMantineTheme } from '@mantine/core'
import ordinal from 'ordinal'
import { useMemo } from 'react'
import { useMetricFormatting } from './api'

function kmsuffix(value: number): string {
  if (value >= 1000000) {
    return (value / 1000000).toFixed(1) + 'M'
  } else if (value >= 1000) {
    return (value / 1000).toFixed(1) + 'K'
  }
  return value.toFixed(0)
}

export function isOrdinalStatistic(statistic: string) {
  return (
    statistic === 'rank' ||
    statistic === 'rank_ytd' ||
    statistic === 'team_rank' ||
    statistic === 'team_rank_ytd'
  )
}

/**
 * Maps a named color to a hex color value using Mantine theme colors
 * @param colorName - Named color from database (red, orange, yellow, green, blue, purple)
 * @param theme - Mantine theme object
 * @returns Hex color string
 */
function mapColorToHex(
  colorName: string | null | undefined,
  theme: MantineTheme
): string {
  if (!colorName) return 'inherit'

  // Map named colors to Mantine theme colors at shade [7] for consistency
  const colorMap: { [key: string]: string } = {
    red: theme.colors.red[9],
    orange: theme.colors.orange[7],
    yellow: theme.colors.yellow[7],
    green: theme.colors.green[9],
    blue: theme.colors.blue[7],
    purple: theme.colors.violet[7],
  }

  return colorMap[colorName.toLowerCase()] || '#000000'
}

function formatPercent(value: number, decimalPlaces: number = 2): string {
  return (value * 100).toFixed(decimalPlaces) + '%'
}

function findColor(
  value: number,
  theme: MantineTheme,
  thresholds: number[],
  colors: string[]
): string {
  let colorName = null

  if (thresholds.length > 0 && colors.length > 0) {
    // Find the appropriate color based on threshold
    // Thresholds are expected to be sorted in ascending order
    let colorIndex = 0
    for (let i = 0; i < thresholds.length; i++) {
      if (value >= thresholds[i]) {
        colorIndex = i + 1
      } else {
        break
      }
    }

    // Make sure we don't go out of bounds
    colorIndex = Math.min(colorIndex, colors.length - 1)
    colorName = colors[colorIndex]
  }

  return mapColorToHex(colorName, theme)
}

export type FmtOptions = {
  metric: number | string
  component: string
  statistic: string
  value: number | null | undefined
}

export type MetricFormatConfig = {
  key: string
  metric_id: number
  is_percent_metric: boolean
  decimal_places: number
  percent_decimal_places: number
  group_thousands: boolean
  prefix: string
  suffix: string
  suffix_thousands: boolean
  percent_color_thresholds: number[]
  percent_colors: string[]
}

/**
 * Pure function that formats a metric value based on the database formatting configuration
 * Extracted from useFmt to allow usage outside of React components
 */
export function fmt(
  metric: number | string,
  component: string,
  statistic: string,
  value: number | null | undefined,
  formatting: MetricFormatConfig[],
  override?: Partial<MetricFormatConfig>
): string {
  if (value == null) {
    return '-'
  }

  if (isOrdinalStatistic(statistic)) {
    return ordinal(value)
  }

  if (statistic === 'achieved' || statistic === 'achieved_ytd') {
    return value.toFixed(0)
  }

  // Find formatting config for this metric
  let config = formatting.find((f) =>
    typeof metric === 'string' ? f.key === metric : f.metric_id === metric
  )

  const isValueStatistic = statistic === 'value' || statistic === 'value_ytd'

  if (component === 'score' && isValueStatistic) {
    return value.toFixed(0)
  }

  if (!config) {
    if (component === 'percent' && isValueStatistic) {
      return formatPercent(value, override?.percent_decimal_places || 2)
    } else {
      return value.toFixed(override?.decimal_places || 0)
    }
  }

  config = { ...config, ...override }

  const isPercent =
    (component === 'percent' && isValueStatistic) ||
    ((component === 'actual' || component === 'target') &&
      isValueStatistic &&
      config.is_percent_metric)

  if (isPercent) {
    const decimalPlaces =
      component === 'actual'
        ? config.decimal_places
        : config.percent_decimal_places
    return formatPercent(value, decimalPlaces)
  }

  if (config.suffix_thousands) {
    return config.prefix + kmsuffix(value)
  } else {
    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: config.decimal_places,
      maximumFractionDigits: config.decimal_places,
      useGrouping: config.group_thousands,
    })
    return config.prefix + formatter.format(value) + config.suffix
  }
}

/**
 * Hook that formats a metric value based on the database formatting configuration
 */
export function useFmt(
  options: FmtOptions,
  override?: Partial<MetricFormatConfig>
): string {
  const { metric, component, statistic, value } = options
  const { data: formatting } = useMetricFormatting()

  return useMemo(
    () => fmt(metric, component, statistic, value, formatting, override),
    [metric, component, statistic, value, formatting]
  )
}

export function fmtColor(
  metric: number | string,
  component: string,
  statistic: string,
  value: number | null | undefined,
  formatting: MetricFormatConfig[],
  theme: MantineTheme
): string | undefined {
  if (value == null) {
    return undefined
  }

  // Find formatting config for this metric
  const config = formatting.find((f) =>
    typeof metric === 'string' ? f.key === metric : f.metric_id === metric
  )
  if (!config) {
    return undefined
  }

  if (
    component === 'percent' &&
    (statistic === 'value' || statistic === 'value_ytd')
  ) {
    return findColor(
      value,
      theme,
      config.percent_color_thresholds,
      config.percent_colors
    )
  } else {
    return undefined
  }
}

export function useFmtColor(options: FmtOptions): string | undefined {
  const { metric, component, statistic, value } = options
  const theme = useMantineTheme()
  const { data: formatting } = useMetricFormatting()

  return useMemo(
    () => fmtColor(metric, component, statistic, value, formatting, theme),
    [metric, component, statistic, value, formatting, theme]
  )
}

interface FmtOptionsProps extends FmtOptions {
  className?: string
  override?: Partial<MetricFormatConfig>
}

export function FmtColored(props: FmtOptionsProps) {
  const formatted = useFmt(props, props.override)
  const color = useFmtColor(props)
  return (
    <span className={props.className} style={{ color }}>
      {formatted}
    </span>
  )
}

export function Fmt(props: FmtOptionsProps) {
  const formatted = useFmt(props, props.override)
  return <span className={props.className}>{formatted}</span>
}
